import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { Fragment } from 'react'
import { FieldErrors, UseFormRegister, UseFormTrigger } from 'react-hook-form'
import Cancel from '../icons/Solid/Icon/Solid/Cancel.svg'

const FieldSet = styled.fieldset(
  ({ theme: { spacing } }) => css`
    border: 0;
    margin-bottom: ${spacing.x3}px;
    padding: 0;
  `,
)
const Title = styled.span(
  ({ theme: { spacing } }) => css`
    display: block;
    font-size: 16px;
    font-weight: bold;
    margin-bottom: ${spacing.x3}px;
  `,
)
const Input = styled.input`
  display: none;
`
const Label = styled.label(
  ({ theme: { colors, spacing } }) => css`
    cursor: pointer;
    display: block;
    line-height: ${spacing.x3}px;
    margin-bottom: ${spacing.x2}px;
    outline: 0;
    padding-left: ${spacing.x3 + spacing.x1}px;
    position: relative;

    :last-of-type {
      margin-bottom: 0;
    }

    ::before {
      border: 1px solid ${colors.neutral2};
      border-radius: 100%;
      content: '';
      height: ${spacing.x3}px;
      left: 0;
      position: absolute;
      top: 0;
      width: ${spacing.x3}px;
    }

    ::after {
      background: ${colors.primaryPurple};
      border-radius: 100%;
      content: '';
      height: 14px;
      left: 5px;
      opacity: 0;
      position: absolute;
      top: 5px;
      transition: opacity 200ms cubic-bezier(0.2, 0.9, 0.42, 1);
      width: 14px;
    }

    :focus,
    :hover {
      ::before {
        border-color: ${colors.primaryPurple};
        box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.15);
      }
    }

    ${Input}:checked + & {
      ::after {
        opacity: 1;
      }
    }
  `,
)
const Required = styled.span(
  ({ theme: { colors } }) => css`
    color: ${colors.error};
  `,
)

const ErrorIcon = styled(Cancel)(
  ({ theme }) => css`
    fill: ${theme.colors.error};
    margin-right: ${theme.spacing.x1}px;
  `,
)

const Error = styled.p(
  ({ theme }) => css`
    color: ${theme.colors.error};
    line-height: 1.57;
    margin: ${theme.spacing.x1}px 0 0;
  `,
)

interface IFormInputs {
  [key: string]: string | boolean | number
  gRecaptchaResponse: string
}

export interface FormFieldRadioGroupProps {
  errorMessage?: string | null
  id: string
  label?: string | null
  name?: string | null
  options?: { id: string; label: string; value: string }[]
  required?: boolean
  register: UseFormRegister<IFormInputs>
  errors: FieldErrors<IFormInputs>
  trigger: UseFormTrigger<IFormInputs>
}

const FormFieldRadioGroup = ({
  errors,
  errorMessage,
  id,
  label,
  name,
  options,
  register,
  required,
  trigger,
}: FormFieldRadioGroupProps) => {
  if (name)
    return (
      <FieldSet>
        <Title>
          {label}
          {required && <Required>*</Required>}
        </Title>
        {options?.map(({ id: optionID, label, value }) => (
          <Fragment key={`${id}-${optionID}`}>
            <Input
              {...register(name, {
                ...(required && { required }),
              })}
              {...{
                'aria-invalid': Boolean(errors[name]),
              }}
              id={`${id}-${optionID}`}
              type="radio"
              value={value}
              onClick={() => trigger(name)}
            />
            <Label htmlFor={`${id}-${optionID}`} tabIndex={0}>
              {label}
            </Label>
          </Fragment>
        ))}
        {errors[name] && (
          <Error role="alert">
            <ErrorIcon />
            {errorMessage}
          </Error>
        )}
      </FieldSet>
    )
  return null
}

export default FormFieldRadioGroup
